import { useEffect, useState } from "react";
import { useAppDispatch, useSelector } from "../../redux-store";
import {
  selectFormDataForWhenOpeningModal,
  selectIsSaving,
  selectResetFormData,
} from "../../ducks/app/schedule-cargo-template-dialog/selectors";
import * as scheduleCargoTemplateDialogActions from "../../ducks/app/schedule-cargo-template-dialog";
import { loadScheduleCargoTemplate } from "../../ducks/app/schedule-cargo-template-dialog";
import CreateEditShipmentDialog from "./CreateEditShipmentDialog";
import { useStableNavigate } from "../StableNavigateContext";
import { CargoTemplateData } from "../../ducks/app/schedule-cargo-template-dialog/types";
import { useLocation } from "react-router-dom";
import { loadSchedule } from "../../ducks/data/schedules";
import { CargoFormData } from "../../ducks/app/cargo-dialog/types";
import { loadProducts } from "../../ducks/data/products";
import { CargoModalEntityType } from "./CreateEditShipmentDialog/CargoModalEntityType";

type EditShipmentTemplateProps = {
  onClose: (props: {
    formData?: CargoFormData;
    cargoTemplateId?: string;
  }) => void;
  cargoTemplateId?: string;
  routeTemplateId: string | null;
  routeId?: string;
};

const AddEditScheduleCargoTemplateModalWrapper = (
  props: EditShipmentTemplateProps
) => {
  const { cargoTemplateId, routeTemplateId } = props;
  const [cargoTemplateData, setCargoTemplateData] =
    useState<CargoTemplateData | null>(null);
  const resetData = useSelector(selectResetFormData);
  const isSaving = useSelector(selectIsSaving);
  const dispatch = useAppDispatch();
  const formStartingData = useSelector(selectFormDataForWhenOpeningModal);
  const navigate = useStableNavigate();
  const { hash } = useLocation();
  const params = new URLSearchParams(hash.substring(1));
  const scheduleId = params.get("scheduleId") || "";

  const onClose = ({
    formData,
    cargoTemplateId,
  }: {
    formData?: CargoFormData;
    cargoTemplateId?: string;
  }) => {
    setCargoTemplateData(null);
    props.onClose({ formData, cargoTemplateId });
  };

  useEffect(() => {
    if (cargoTemplateId) {
      dispatch(loadScheduleCargoTemplate(cargoTemplateId)).then((result) => {
        if (result.payload) {
          const data = (result.payload as any).data as CargoTemplateData; // TODO: can't infer correct type
          setCargoTemplateData(data);
        }
      });
    }
  }, [cargoTemplateId, dispatch]);

  const handleSubmit = async (
    formData: CargoTemplateData & {
      keepAlive?: boolean;
      submitType: "CREATE" | "UPDATE" | "DUPLICATE" | "CREATE_AND_MAKE_COPY";
      dropDeletedExtraCo2Types?: boolean;
    }
  ) => {
    const res = await dispatch(
      scheduleCargoTemplateDialogActions.submit({
        ...formData,
        scheduleId,
        routeTemplateId,
      })
    ).unwrap();
    // Refused (e.g. deleted extra CO2 types, or someone else saved it
    // meanwhile): stay open, the dialog asks or handles it.
    if ("error" in res && res.error) {
      return res;
    }
    if (!res.keepAlive) {
      setCargoTemplateData(null);
      navigate({ hash: "" });
    }
    dispatch(loadSchedule(scheduleId));

    return res;
  };

  const handleDelete = async () => {
    if (!cargoTemplateId) {
      console.error("No cargo template id to delete");
      return;
    }
    dispatch(
      scheduleCargoTemplateDialogActions.deleteCargoTemplate({
        cargoTemplateId,
        scheduleId,
      })
    );
    // TODO: only navigate and close on success, how to do this?
    // TODO: consider moving into the redux actions layer
    navigate({ hash: "" }, { replace: true });
  };

  const [productsLoaded, setProductsLoaded] = useState(false);
  useEffect(() => {
    if (productsLoaded) {
      return;
    }
    dispatch(loadProducts())
      .unwrap()
      .then(() => setProductsLoaded(true))
      // loadProducts has already reported the failure and shown the toast;
      // without this the rejection escapes as an unhandled one (DISPATCHER-4).
      .catch(() => {});
  }, [dispatch, productsLoaded]);

  return productsLoaded &&
    (cargoTemplateData || (!cargoTemplateId && formStartingData)) ? (
    <CreateEditShipmentDialog
      resetData={resetData!}
      initialData={cargoTemplateData || formStartingData!}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
      onClose={onClose}
      isSaving={isSaving}
      createNew={!cargoTemplateId}
      entityType={CargoModalEntityType.SCHEDULE_CARGO_TEMPLATE}
    />
  ) : null;
};

export default AddEditScheduleCargoTemplateModalWrapper;
